import { useMemo } from 'react';
import { DataTable } from '@/components/common';
import { getVenueColumns } from './VenueTable.columns';
import type { Venue } from '../../types';

interface VenueTableProps {
  data: Venue[];
  isLoading?: boolean;
  error?: Error | null;
  onViewDetails?: (venue: Venue) => void;
}

export function VenueTable({
  data,
  isLoading = false,
  error = null,
  onViewDetails,
}: VenueTableProps) {
  const columns = useMemo(() => getVenueColumns(), []);

  // Add onViewDetails to each venue
  const dataWithHandlers = useMemo(() => 
    data.map(venue => ({
      ...venue,
      onViewDetails
    })),
    [data, onViewDetails]
  );

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading venues...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-2">Failed to load venues</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-dashed rounded-lg">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">No venues found</p>
          <p className="text-sm text-muted-foreground">
            Create your first venue to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <DataTable
      data={dataWithHandlers}
      columns={columns}
      config={{
        enableColumnVisibility: true,
        enableSorting: true,
        enableFiltering: true,
        defaultPageSize: 10,
        pageSizeOptions: [5, 10, 20, 50],
        searchPlaceholder: 'Search venues...',
      }}
    />
  );
}